'use client';

import React from 'react';
import classnames from 'classnames';
import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import Iconography, { IconographyProps } from '../Iconography/Iconography';

import styles from './Badge.module.scss';

type BadgeColor =
  | 'neutral'
  | 'brand'
  | 'gray'
  | 'warning'
  | 'success'
  | 'error'
  | 'informative'
  | 'cream'
  | 'darktaupe'
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'fuchsia'
  | 'pink';

type BadgeType = 'primary' | 'secondary';
type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';

export interface BadgeProps {
  label?: string;
  icon?: Pick<IconographyProps, 'category' | 'name'>;
  type?: BadgeType;
  color?: BadgeColor;
  theme?: Theme;
  direction?: Direction;
  minWidth?: string;
  className?: string;
  style?: React.CSSProperties;
}

const Badge: React.FC<BadgeProps> = ({
  label,
  icon,
  type = 'primary',
  color = 'brand',
  theme = 'dark',
  direction = 'ltr',
  minWidth,
  className,
  style,
}) => {
  return (
    <Box
      className={classnames(styles.badge, styles[`${theme}_${type}_${color}`], className)}
      pt={4}
      pl={8}
      pb={4}
      pr={8}
      // eslint-disable-next-line react/forbid-component-props -- Dynamic minWidth requires inline style
      style={{
        minWidth,
        display: minWidth ? 'inline-flex' : undefined,
        justifyContent: minWidth ? 'center' : undefined,
        ...style,
      }}
    >
      {icon && <Iconography category={icon.category} name={icon.name} size={20} />}
      <Typography variant="body" size="small" direction={direction}>
        {label}
      </Typography>
    </Box>
  );
};

Badge.displayName = 'Badge';

export default Badge;
